/**
 * 협력사 오더목록 페이지 ($j.Pages.Partner.OrderList / body data-page-name="OrderList")
 *
 * 레거시 public/scs/main2.php(범용) · main3.php(머큐리) 대체.
 *
 * 목록 테이블과 검색필터는 관리자 접수(/adm/orders)와 공용 모듈을 쓴다:
 *   $j.F.Order.ListTable  — DataTables 컬럼 렌더 / 행확장 / 지도·사진 팝업
 *   $j.F.Order.SearchOpt  — opt 버튼 토글 → opt 문자열 → ajax 페이로드
 *
 * 관리자 전용 기능(일괄수금완료·푸시·확인서·문자접수·환원예정·지정미확인·홀드·회원포털·오더핸들)은
 * 가져오지 않는다. 대신 협력사 전용으로 신규발주(기사선택)·엑셀·대리로그인을 둔다.
 *
 * 소속 스코프(내 소속 오더만 조회)는 서버(OrderListService::partnerScope)가 강제하므로
 * 이 화면은 본사(x) 셀렉트를 보내지 않는다.
 */
import Form from '../../Common/Form';
import Util from '../../Common/Util';

export default class OrderList {
    constructor() {
        // ─── 검색/정렬 상태 (SearchOpt.params 가 읽는다) ───
        this.sortField   = '';       // 정렬 컬럼 (thead th[data-col])
        this.sortOrder   = 'desc';   // asc | desc
        this.dateType    = 'n';      // n=오더생성기준, d=작업일기준
        this.specialCond = '';       // 협력사는 특수조건(환원예정/지정미확인) 없음 — 항상 빈값
        this.$table      = null;

        // ─── 서버 계산값 (#page-data — Partner\OrderController::normalIndex) ───
        const d = JSON.parse(document.getElementById('page-data')?.textContent || '{}');
        this.defDay_st        = d.defDay_st || '';
        this.defDay_en        = d.defDay_en || '';
        this.sosok            = d.partnerSosok || '';
        this.loginType        = d.partnerLoginType || '';
        this.showGisaSelect   = !!d.showGisaSelect;
        this.showCenterSelect = !!d.showCenterSelect;
        this.insuranceCols    = !!d.insuranceCols;
        // 오더 확인체크/메모 — 관리자가 협력사 화면을 볼 때만 (서버가 get_mbno_related_admin 으로 판정)
        this.canMemo          = !!d.canMemo;
    }

    initialize() {
        this.exposeGlobals();
        this.initPage();
        this.initHandler();
    }

    /**
     * 외부 창/iframe 호환 브리지 — 별도 창이 parent.orderDetail_show 를 부른다.
     * 행 확장 상세(adm/pages/order/detail.blade.php)의 사진·전화번호 클릭은 ListTable.bindDetail 위임.
     */
    exposeGlobals() {
        window.orderDetail_show = (val) => $j.F.Order.ListTable.orderDetailShow(val);
        $j.F.Order.ListTable.bindDetail((ono, n) => $j.F.Order.ListTable.showPic(ono, n));
    }

    initPage() {
        $('#d1').val(this.defDay_st);
        $('#d2').val(this.defDay_en);
        this.initDataTable();
    }

    initDataTable() {
        const self = this;

        this.$table = $j.F.Order.ListTable.create({
            selector : '#order-table',
            url      : '/p/ajax/orders/list',
            detailUrl: '/p/ajax/order/detail',
            columns  : this.columnKeys(),
            getSearch: () => self.searchParams(),

            // 관리자 전용 렌더는 전부 off — 홀드 input / 회원포털 링크 / 오더핸들·매칭내역 팝업
            canHold  : false,
            canPortal: false,
            canHandle: false,
            canMatch : false,
            // 오더 확인체크 아이콘(작업메모 컬럼) — 관리자가 협력사 화면을 볼 때만
            canMemo  : this.canMemo,

            onSort: (col) => {
                if (self.sortField === col) {
                    self.sortOrder = self.sortOrder === 'asc' ? 'desc' : 'asc';
                } else {
                    self.sortField = col;
                    self.sortOrder = 'desc';
                }
                self.$table.ajax.reload();
            },

            onLoaded: (json) => {
                const tp = json.totalPrice || 0;
                const tc = json.recordsTotal || 0;
                $('#totalPriceInfo').html('총금액 : ' + Util.formatNumber(tp) + '원 (' + Util.formatNumber(tc) + '건)'
                    + $j.F.Order.SearchOpt.memberSearchHtml(json.searchInfo));
            },
        });

        this.moveDtFooter();

        // 표만 스크롤되는 구조라 스크롤이 남아있으면 다음 페이지/새 검색결과가 중간부터 보인다
        this.$table.on('draw', () => {
            const el = document.getElementById('orderlistTableHolderDiv');
            if (el) el.scrollTop = 0;
        });
    }

    /**
     * DataTables 가 표 아래(=스크롤 영역 안)에 그린 "건수·페이징" 줄을 하단 고정바(#orderPaging)로 옮긴다.
     * 노드째 옮기므로 DataTables 가 그 참조로 계속 갱신한다 — 관리자 OrderList.moveDtFooter() 와 같다.
     */
    moveDtFooter() {
        $('#orderPaging').empty();
        $('#order-table_wrapper .dt-layout-row').has('.dt-paging').appendTo('#orderPaging');
    }

    /**
     * 표시할 컬럼 (블레이드 thead 순서와 반드시 일치해야 한다).
     * 관리자에 있는 발주/수주 회원코드 컬럼은 뺐고, 머큐리(보험 소속)면 meta 컬럼 4개를 붙인다.
     */
    columnKeys() {
        const keys = ['orderNum', 'carType', 'balName', 'gisa', 'center', 'address',
                      'workTime', 'dayType', 'workHour', 'addHour', 'price', 'feeRate',
                      'carRange', 'option', 'howtopay', 'status', 'workerName',
                      'fieldNumber', 'carPlate', 'bonsaCol', 'memo'];
        if (this.insuranceCols) keys.push('insCompany', 'insTeam', 'insManager', 'insPhone');
        return keys;
    }

    initHandler() {
        this.checkSerKeys();

        // 필터 영역(opt 버튼·그룹·인풋·검색/엑셀/신규발주) 이벤트 위임. 블레이드엔 data-act 만 있다.
        $j.F.Order.SearchOpt.bindFilters(this);

        // 지사 → 센터 연동. 본사/지사 코드는 세션 소속에서 나온다 (클라이언트가 정하지 않는다).
        if (this.showGisaSelect) {
            Form.registerGisaHandler('y', 'z');
            Form.updateGisaSelectBox(this.sosok, 'y', 'z');
        } else if (this.showCenterSelect) {
            Form.updateCenterSelectBox(this.sosok, 'z');
        }

        if (!$j.Common.Util.isMobile()) {
            $('#d1,#d2').mobiscroll().datepicker({
                controls: ['calendar'],
                dateFormat: 'YYYY-MM-DD',
                locale: mobiscroll.localeKo,
                buttons: [],
            });
        }

        // 현장/메모 칸 = 오더 통합검색. 관리메모는 협력사에 노출하지 않으므로 안내에서도 뺀다.
        $j.F.Order.SearchHelp.init({
            isAdmin: false,
            onChange: () => this.checkSerKeys(),
        });
    }

    // ==================== 검색 / 필터 (공용 모듈 위임) ====================
    /** 현재 검색 UI 상태 → 서버 페이로드. */
    searchParams() {
        const p = $j.F.Order.SearchOpt.params({
            dateType: this.dateType,
            specialCond: this.specialCond,
            sortField: this.sortField,
            sortOrder: this.sortOrder,
        });
        delete p.x; // 본사 셀렉트는 협력사에 없다 (서버가 세션 소속으로 강제)
        return p;
    }

    /** 발주자/수주자는 인풋 값 자체가 검색기준('이재일#2831')이라 별도 동기화가 없다. */
    doSearch() {
        this.$table.ajax.reload();
    }

    toggleButton(tar) { $j.F.Order.SearchOpt.toggleButton(tar); }
    toggleGroup(n)    { $j.F.Order.SearchOpt.toggleGroup(n); }

    /** 기간 검색 기준 토글 (오더생성 ↔ 작업일). */
    toggleOdrDate() {
        if (this.dateType === 'd') {
            this.dateType = 'n';
            $('#topchbox_a').html('오더생성기준');
        } else {
            this.dateType = 'd';
            $('#topchbox_a').html('작업일기준');
        }
    }

    /** 인풋 값 초기화 — 클릭하면 비운다 (blade: data-clear-on-click). 구 CCC(). */
    clearInput(tar) {
        $j.F.Order.SearchOpt.clearInput(tar);
        this.checkSerKeys();
    }

    checkSerKeys() {
        $j.F.Order.SearchOpt.checkSerKeys(this.defDay_st, this.defDay_en,
            ['c', 'y', 'z', 'stx', 'n', 'wb', 'ws']);
    }

    // ==================== 협력사 전용 ====================
    /** 엑셀 다운로드 — 서버가 직전 목록 SQL(ExcelRelay::ORDER)을 그대로 쓴다. */
    saveToExcel() {
        document.location.href = '/p/excel/orders';
    }

    /** 신규발주 — 소속 기사를 고른 뒤 그 기사로 대리로그인해 모바일 발주화면으로 넘어간다. */
    newOrder() {
        window.open('/p/orders/new', 'svcNewOrder',
            'toolbar=no,status=no,width=700,height=800,directories=no,scrollbars=yes,location=no,resizable=yes,menubar=no');
    }

}
